



import React, { useState, useEffect, Suspense } from "react"
import {
  Container,
  TextField,
  Card,
  CardMedia,
  CardContent,
  Button,
  Grid,
  Typography,
  MenuItem,
  Box,
  Paper,
  IconButton,
  Snackbar,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { styled } from "@mui/material/styles"
import { CloudUpload, Close } from "@mui/icons-material"
import { updateBlogPost } from "../Api/updatePost"
import { postBlog } from "../Api/blogPost"
import { deletePost } from "../Api/deletePost" // Make sure to create this API function
import "react-quill/dist/quill.snow.css"
import { useNavigate } from "react-router-dom"
import { useLocation } from "react-router-dom"

const ReactQuill = React.lazy(() => import("react-quill"))

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

const StyledReactQuill = styled("div")(({ theme }) => ({
  "& .ql-toolbar": {
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: "none",
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  "& .ql-container": {
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    minHeight: "200px",
  },
}))

const AdminPanel = () => {
  const [newBlog, setNewBlog] = useState({
    title: "",
    description: "",
    image: null,
    category: "",
    user_Id:"",
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [quillLoaded, setQuillLoaded] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const postData = location.state?.postData || {}

  const categories = [
    "Health",
    "Technology",
    "Finance",
    "Travel",
    "Food",
    "Lifestyle",
    "Fashion",
    "Entertainment",
    "Career",
  ]

  useEffect(() => {
    if (postData && Object.keys(postData).length > 0) {
      setNewBlog({
        title: postData.title || "",
        description: postData.content || "",
        category: postData.category || "",
        image: null,
      })
      if (postData.blog_img) {
        setImagePreview(`http://localhost:5000/uploads/${postData.blog_img}`)
      }
      setIsUpdating(true)
    }
  }, [postData])

  useEffect(() => {
    setQuillLoaded(true)
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true"
    if (!isLoggedIn) {
      navigate("/", { replace: true })
    }
  }, [navigate])

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate("/", { replace: true })
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn")
    navigate("/", { replace: true })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setNewBlog({
      ...newBlog,
      [name]: value,
    })
  }

  const handleDescriptionChange = (content) => {
    setNewBlog((prevState) => ({
      ...prevState,
      description: content,
    }))
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setNewBlog({
        ...newBlog,
        image: file,
      })

      const previewURL = URL.createObjectURL(file)
      setImagePreview(previewURL)
    }
  }
  const user_Id=localStorage.getItem("userId");
// console.log("user_Id",user_Id);

const handleUpdatePost = async () => {
  const updatedData = {
      title: newBlog.title,
      content: newBlog.description,
      category: newBlog.category,
      blog_img: newBlog.image?.url || " ",
  };

  try {
      // Ensure postData._id is passed in the URL
      const updatedPost = await updateBlogPost(postData._id, updatedData);
      console.log("Post updated successfully:", updatedPost);
      setSnackbar({ open: true, message: "Post updated successfully!", severity: "success" });
      setTimeout(() => {
          navigate("/", { replace: true });
      }, 2000);
  } catch (error) {
      console.error("Error updating post:", error);
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: "error" });
  }
};


  const handleCreatePost = async (formData) => {
    try {
      await postBlog(formData)
      setSnackbar({
        open: true,
        message: "Blog posted successfully!",
        severity: "success",
      })
      setNewBlog({ title: "", description: "", image: null, category: "" })
      setTimeout(() => {
        navigate("/", { replace: true })
      }, 2000)
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to post blog: ${error.message}`,
        severity: "error",
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (newBlog.title && newBlog.description && newBlog.category && user_Id) {
      const formData = new FormData()
      formData.append("title", newBlog.title)
      formData.append("content", newBlog.description)
      formData.append("category", newBlog.category)
      formData.append("user_id", user_Id)  // Add user_id to the form data
      if (newBlog.image) {
        formData.append("blog_img", newBlog.image)
      }

      setIsSubmitting(true)

      try {
        if (isUpdating) {
          await handleUpdatePost()
        } else {
          await handleCreatePost(formData)
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Failed to ${isUpdating ? "update" : "post"} blog: ${error.message}`,
          severity: "error",
        })
      } finally {
        setIsSubmitting(false)
      }
    } else {
      setSnackbar({
        open: true,
        message: "Please fill out all required fields.",
        severity: "warning",
      })
    }
  }

  const handleDeletePost = async () => {
    if (postData && postData._id) {
      try {
        await deletePost(postData._id)
        setSnackbar({
          open: true,
          message: "Post deleted successfully!",
          severity: "success",
        })
        setTimeout(() => {
          navigate("/", { replace: true })
        }, 2000)
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Failed to delete post: ${error.message}`,
          severity: "error",
        })
      }
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Button variant="outlined" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom textAlign="center" color="primary" sx={{ mb: 3 }}>
          {isUpdating ? "Edit Blog" : "Post a New Blog"}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={newBlog.title}
                onChange={handleChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                name="category"
                value={newBlog.category}
                onChange={handleChange}
                variant="outlined"
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Description
              </Typography>
              <StyledReactQuill>
                {quillLoaded && (
                  <Suspense fallback={<div>Loading editor...</div>}>
                    <ReactQuill
                      value={newBlog.description}
                      onChange={handleDescriptionChange}
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link"],
                          ["clean"],
                        ],
                      }}
                      formats={["bold", "italic", "underline", "list", "bullet", "link"]}
                      placeholder="Write your blog post here..."
                    />
                  </Suspense>
                )}
              </StyledReactQuill>
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" color="primary" startIcon={<CloudUpload />}>
                Upload Image
                <VisuallyHiddenInput type="file" onChange={handleFileChange} accept="image/*" />
              </Button>
              {imagePreview && (
                <Card sx={{ maxWidth: 300, mt: 2 }}>
                  <CardMedia component="img" image={imagePreview} alt="Image Preview" />
                  <CardContent>
                    <IconButton onClick={() => setImagePreview(null)}>
                      <Close />
                    </IconButton>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}>
            {isUpdating && (
              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleDeletePost}
                disabled={isSubmitting}
              >
                Delete Post
              </Button>
            )}
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : isUpdating ? "Update Post" : "Post Blog"}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Container>
  )
}

export default AdminPanel

